/*
 * Presentation Templates — the Asset Library's "Presentation Templates" tab
 * content. Deliberately standalone: this whole feature (this component
 * included) never imports from slide-templates — see this repo's
 * presentation-templates/services/presentationTemplatesApi.ts module header
 * for why. Slide Templates' SlideTemplatesPanel is read only as a reference
 * for the visual/architectural pattern; every helper below (wireframe
 * drawing, card shells) is this component's own copy.
 *
 * TWO RENDER MODES
 * Grid mode (default): one card per presentation — a wireframe drawn from its
 * FIRST slide's thumbnail (the presentation's own representative preview), a
 * Download icon in its top-right corner, its name, and "{N} slides · click to
 * preview". Clicking the card is NAVIGATION (openPresentation), never a
 * selection toggle — a presentation itself isn't insertable, only its
 * individual slides are. The Download icon saves the whole deck as a .pptx
 * without opening it (BRD 8.4).
 *
 * Preview mode (a presentation is open): a "Back" button returns to the grid,
 * a header shows the presentation's name (one line, ellipsised) + slide count,
 * a "Select all"/"Deselect all" link once at least one slide is picked (BRD
 * 8.6), and a grid of per-slide cards ("Slide 1", "Slide 2", …) each toggling
 * selection on click.
 *
 * The footer's "Insert into deck" (driven by the shared AssetLibraryContent
 * footer via the imperative `insertSelected` handle + `onSelectionStateChange`
 * callback, same pattern SlideTemplatesPanel uses) always acts on every
 * selected slide across every presentation, not just the one being previewed
 * — selection is a flat set of globally-unique slide ids.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { usePresentationTemplateThumbnail } from "../hooks/usePresentationTemplateThumbnail";
import {
  usePresentationTemplates,
  type PresentationTemplatesLoadState,
  type UsePresentationTemplatesOptions,
} from "../hooks/usePresentationTemplates";
import type {
  PresentationSlidePreview,
  PresentationSlidePreviewRole,
  PresentationTemplate,
  PresentationTemplateSlide,
} from "../services/presentationTemplatesApi";
import type { ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import { Notice } from "../../../../../shared/components/Notice";
import { FilesIcon, WarningIcon } from "../../../../../shared/components/icons";
import { SearchIcon } from "../../action-chips/chipIcons";
import { useRequestNewSelection } from "../../asset-library/types";
import styles from "./PresentationTemplatesPanel.module.css";

/** What the tab is showing, so the shared footer can word itself to match. */
export type PresentationTemplatesView = "ready" | "error" | "empty";

export interface PresentationTemplatesSelectionState {
  readonly count: number;
  readonly canInsert: boolean;
  readonly isInserting: boolean;
  readonly view?: PresentationTemplatesView;
  /** Name of the presentation the picks are in, for the "Start a new selection?" warning. */
  readonly sourceName?: string | null;
}

export interface PresentationTemplatesPanelHandle {
  insertSelected: () => Promise<void>;
  /** Drops every pick — the shared footer's "Clear All". */
  clearSelection: () => void;
  /** Opens the presentation the picks are in — "Keep My Picks" returns the user there. */
  revealSelection: () => void;
}

interface PresentationTemplatesPanelProps {
  readonly onSelectionStateChange?: (state: PresentationTemplatesSelectionState) => void;
  /**
   * Where the manifest comes from and how a selection is committed — see
   * hooks/usePresentationTemplates.ts. Omit it for the in-pane Asset Library
   * (fetch + insert locally); the Office Dialog window passes the
   * hand-off/messageParent pair instead, since it has neither a bearer token
   * nor PowerPoint.run().
   */
  readonly templatesOptions?: UsePresentationTemplatesOptions;
}

const PREVIEW_ONLY_HINT = "This slide can be previewed but not inserted yet";

function matchesSearch(presentation: PresentationTemplate, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = `${presentation.name} ${presentation.category ?? ""}`.toLowerCase();
  return haystack.includes(needle);
}

/** BRD 8.2: the live line under the search field, shown only while a query is active. */
function matchCountText(count: number, query: string): string {
  return `${count} ${count === 1 ? "presentation" : "presentations"} match “${query.trim()}”`;
}

/** The header button's label: the picks' count when only some are chosen, else the whole pack. */
function downloadPackLabel(selected: number, total: number): string {
  if (selected === 0 || selected >= total) return "Download Pack";
  return selected === 1 ? "Download Slide" : `Download ${selected} Slides`;
}

/** Per-role class, so the wireframe reads as a layout and not a grey mosaic. */
const ROLE_CLASS: Readonly<Record<PresentationSlidePreviewRole, string | undefined>> = {
  title: styles.blockTitle,
  body: styles.blockBody,
  image: styles.blockImage,
  chart: styles.blockChart,
  table: styles.blockTable,
  other: styles.blockOther,
};

/**
 * The placeholder rectangles, drawn as a wireframe.
 *
 * The viewBox is a flat 0–100 square with `preserveAspectRatio="none"`: blocks
 * are already percentages of their own axis, so letting each axis stretch
 * independently to the (correctly-proportioned) box is what puts them in the
 * right place. Only the 1-unit corner radius distorts, which is invisible at
 * thumbnail size.
 */
function LayoutWireframe({ preview }: Readonly<{ preview: PresentationSlidePreview }>) {
  return (
    <svg className={styles.wireframe} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      {preview.blocks.map((block, index) => (
        <rect
          // Blocks have no ids of their own; the rect's own geometry is what
          // makes it unique within a card (identical ones are deduped upstream).
          key={`${block.role}-${block.x}-${block.y}-${block.w}-${block.h}-${index}`}
          className={ROLE_CLASS[block.role] ?? ROLE_CLASS.other}
          x={block.x}
          y={block.y}
          width={block.w}
          height={block.h}
          rx={1}
        />
      ))}
    </svg>
  );
}

/**
 * Manifest thumbnail, wireframe, or a bare document-icon placeholder — in
 * that priority order. `hasPhoto` suppresses the icon once a real photo is
 * about to be layered on top: a card with nothing left to draw behind a photo
 * that already fills it shouldn't show one.
 */
function ThumbnailBaseLayer({
  thumbnailUrl,
  preview,
  hasPhoto,
}: Readonly<{ thumbnailUrl: string | null; preview: PresentationSlidePreview | null; hasPhoto: boolean }>) {
  if (thumbnailUrl) {
    return <img className={styles.thumbnailImg} src={thumbnailUrl} alt="" loading="lazy" />;
  }
  if (preview) {
    return <LayoutWireframe preview={preview} />;
  }
  if (hasPhoto) {
    return null;
  }
  return (
    <div className={styles.thumbnailFallback} aria-hidden="true">
      <span>🗎</span>
    </div>
  );
}

/**
 * Base layer (manifest thumbnail, wireframe, or a bare placeholder) plus, once
 * the card scrolls into view, the slide's own real embedded photo(s) laid on
 * top — see hooks/usePresentationTemplateThumbnail.ts for why this is lazy
 * and ../shared/services/pptxSlideImageExtraction.ts for why it isn't sourced
 * from the manifest.
 */
function SlideThumbnail({
  slide,
  loadThumbnail,
}: Readonly<{
  slide: PresentationTemplateSlide;
  loadThumbnail?: (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const images = usePresentationTemplateThumbnail(slide, rootRef, loadThumbnail);

  // Once a card has its real photo(s), the wireframe's "image"-role rects are
  // just a stale placeholder for the same region — drawing both reads as a
  // jumbled mix of real content and abstract blocks, not a clean preview.
  const wireframePreview = useMemo(() => {
    if (images.length === 0 || !slide.preview) return slide.preview;
    const blocks = slide.preview.blocks.filter((block) => block.role !== "image");
    return blocks.length > 0 ? { ...slide.preview, blocks } : null;
  }, [images.length, slide.preview]);

  return (
    <div ref={rootRef} className={styles.thumbnailStack}>
      <ThumbnailBaseLayer thumbnailUrl={slide.thumbnailUrl} preview={wireframePreview} hasPhoto={images.length > 0} />
      {images.map((image, index) => (
        <img
          // Extracted fresh per render from the slide's own picture list —
          // there's no stable id, and position + source together are unique.
          key={`${image.xPct}-${image.yPct}-${index}`}
          className={styles.thumbnailPhoto}
          src={image.dataUrl}
          alt=""
          style={{ left: `${image.xPct}%`, top: `${image.yPct}%`, width: `${image.wPct}%`, height: `${image.hPct}%` }}
        />
      ))}
    </div>
  );
}

/**
 * `onOpen` (navigate into the slide grid) and `onDownload` (save the whole
 * deck, no navigation) are two independent actions on the same card. A
 * `<button>` can't contain another interactive `<button>`, so the Download
 * icon is a sibling `<button>`, layered over the thumbnail's top-right corner
 * via `.cardWrap`/`.downloadBtn`'s absolute positioning, rather than nested
 * inside the open button — both stay real, natively keyboard- and
 * screen-reader-operable buttons this way, and a click on the icon never
 * reaches the open button.
 */
function PresentationCard({
  presentation,
  onOpen,
  onDownload,
  downloadDisabled,
}: Readonly<{
  presentation: PresentationTemplate;
  onOpen: (id: string) => void;
  onDownload: (id: string) => void;
  downloadDisabled: boolean;
}>) {
  const firstSlide = presentation.slides[0] ?? null;
  const slideCountLabel = presentation.slides.length === 1 ? "1 slide" : `${presentation.slides.length} slides`;

  return (
    <div className={styles.cardWrap}>
      <button type="button" className={styles.card} title={presentation.name} onClick={() => onOpen(presentation.id)}>
        <div className={styles.thumbnail}>
          <div className={styles.thumbnailInner}>
            <ThumbnailBaseLayer thumbnailUrl={presentation.thumbnailUrl} preview={firstSlide?.preview ?? null} hasPhoto={false} />
          </div>
        </div>
        <div className={styles.cardFooter}>
          <span className={styles.cardName}>{presentation.name}</span>
          <span className={styles.cardSubtext}>{`${slideCountLabel} · click to preview`}</span>
        </div>
      </button>
      <button
        type="button"
        className={styles.downloadBtn}
        title="Download presentation"
        aria-label={`Download ${presentation.name}`}
        disabled={downloadDisabled}
        onClick={() => onDownload(presentation.id)}
      >
        <svg aria-hidden="true" viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 3v9M6.5 8.5L10 12l3.5-3.5" />
          <path d="M4 16h12" />
        </svg>
      </button>
    </div>
  );
}

/** BRD 8.11 — nothing matches the search. */
function NoResults({ query, onClear }: Readonly<{ query: string; onClear: () => void }>) {
  return (
    <Notice
      icon={<SearchIcon />}
      title={`No results for “${query}”`}
      message="Try a different word, or clear the search to see everything."
      action={
        <button type="button" className={styles.retryBtn} onClick={onClear}>
          Clear Search
        </button>
      }
    />
  );
}

function SlideCard({
  slide,
  slideIndex,
  selected,
  showPreviewOnlyBadge,
  onToggle,
  loadThumbnail,
}: Readonly<{
  slide: PresentationTemplateSlide;
  slideIndex: number;
  selected: boolean;
  showPreviewOnlyBadge: boolean;
  onToggle: (slide: PresentationTemplateSlide) => void;
  loadThumbnail?: (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const toggle = () => onToggle(slide);

  return (
    <button
      type="button"
      className={selected ? `${styles.card} ${styles.cardSelected}` : styles.card}
      aria-pressed={selected}
      title={slide.description ?? slide.name}
      onClick={toggle}
    >
      <div className={styles.thumbnail}>
        <div className={styles.thumbnailInner}>
          <SlideThumbnail slide={slide} loadThumbnail={loadThumbnail} />
        </div>
        <span className={selected ? `${styles.selectDot} ${styles.selectedTick}` : styles.selectDot} aria-hidden="true">
          {selected ? "✓" : ""}
        </span>
        {showPreviewOnlyBadge && (
          <span className={styles.previewOnlyBadge} title={PREVIEW_ONLY_HINT}>
            Preview only
          </span>
        )}
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.cardName}>{`Slide ${slideIndex}`}</span>
      </div>
    </button>
  );
}

type LoadThumbnail = (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>;

/** How many of `presentation`'s own slides are picked (0 when none is open). */
function countPicked(presentation: PresentationTemplate | null, selectedIds: ReadonlySet<string>): number {
  if (presentation === null) return 0;
  return presentation.slides.filter((slide) => selectedIds.has(slide.id)).length;
}

/** True when the open presentation has slides but none can be inserted. */
function hasOnlyPreviewSlides(presentation: PresentationTemplate | null): boolean {
  if (presentation === null || presentation.slides.length === 0) return false;
  return presentation.slides.every((slide) => !slide.insertable);
}

function resolveView(status: PresentationTemplatesLoadState["status"], presentationCount: number): PresentationTemplatesView {
  if (status === "error") return "error";
  return status === "ready" && presentationCount === 0 ? "empty" : "ready";
}

/** The loading / load-failed / empty screens (BRD 10); null once there is something to show. */
function statusScreen(status: PresentationTemplatesLoadState["status"], view: PresentationTemplatesView, onRetry: () => void): ReactNode {
  if (status === "loading") {
    return (
      <div className={styles.centered}>
        <span className={styles.spinner} aria-hidden="true" />
        <span>Loading presentation templates…</span>
      </div>
    );
  }
  if (view === "error") {
    return (
      <Notice
        icon={<WarningIcon />}
        title="Couldn't load presentation templates"
        message="Check your connection and try again."
        action={
          <button type="button" className={styles.retryBtn} onClick={onRetry}>
            Try Again
          </button>
        }
      />
    );
  }
  if (view === "empty") {
    return (
      <Notice
        icon={<FilesIcon />}
        title="No presentation templates yet"
        message="When your organisation publishes presentation templates, they'll appear here. Ask your brand administrator to add them."
      />
    );
  }
  return null;
}

/** Esc with text in the search box clears it and must NOT close the dialog (BRD 8.2). A window-level
 *  capture listener runs before the host's or any ancestor's handler, which a React onKeyDown can't. */
function useEscapeClearsSearch(inputRef: RefObject<HTMLInputElement>, clear: () => void): void {
  useEffect(() => {
    const swallowEscapeWhileSearching = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !inputRef.current?.value) return;
      event.preventDefault();
      event.stopPropagation();
      clear();
    };
    window.addEventListener("keydown", swallowEscapeWhileSearching, true);
    return () => window.removeEventListener("keydown", swallowEscapeWhileSearching, true);
  }, [inputRef, clear]);
}

interface PickGuardInput {
  readonly presentations: readonly PresentationTemplate[];
  readonly selectedIds: ReadonlySet<string>;
  readonly activePresentation: PresentationTemplate | null;
  readonly toggleSelected: (id: string) => void;
  readonly selectAllSlides: (presentationId: string) => void;
}

/**
 * Where the picks live, and the guarded pick actions. Selection is meant to live in one presentation;
 * adding a pick elsewhere (another tab, or another presentation) asks the shared "Start a new
 * selection?" warning first, while removing a pick is free (BRD 8.10).
 */
function usePickGuard({ presentations, selectedIds, activePresentation, toggleSelected, selectAllSlides }: PickGuardInput) {
  const holders = useMemo(
    () => presentations.filter((presentation) => presentation.slides.some((slide) => selectedIds.has(slide.id))),
    [presentations, selectedIds],
  );
  const requestNewSelection = useRequestNewSelection("presentation-templates");
  const conflictsWith = (presentationId: string) => holders.some((holder) => holder.id !== presentationId);

  const handleToggle = (slide: PresentationTemplateSlide) => {
    if (selectedIds.has(slide.id) || activePresentation === null) {
      toggleSelected(slide.id);
      return;
    }
    requestNewSelection(() => toggleSelected(slide.id), { conflictsWithOwnPicks: conflictsWith(activePresentation.id) });
  };

  const handleSelectAll = (presentationId: string) =>
    requestNewSelection(() => selectAllSlides(presentationId), { conflictsWithOwnPicks: conflictsWith(presentationId) });

  return { sourceName: holders[0]?.name ?? null, holderId: holders[0]?.id ?? null, handleToggle, handleSelectAll };
}

/** Reports the selection to the shared footer whenever it changes. */
function useReportSelection(
  onChange: ((state: PresentationTemplatesSelectionState) => void) | undefined,
  state: PresentationTemplatesSelectionState,
): void {
  const { count, canInsert, isInserting, view, sourceName } = state;
  useEffect(() => {
    onChange?.({ count, canInsert, isInserting, view, sourceName });
  }, [onChange, count, canInsert, isInserting, view, sourceName]);
}

/** The insert outcome: an error wins over the last success message. */
function InsertBanner({ error, status }: Readonly<{ error: string | null; status: string | null }>) {
  if (error) return <div className={styles.insertError}>{error}</div>;
  // The dialog window is the only UI in the real host, so it reports the outcome itself, not a pane toast.
  if (status) return <output className={styles.insertStatus}>{status}</output>;
  return null;
}

function SearchToolbar({
  inputRef,
  search,
  matchCount,
  onChange,
  onClear,
}: Readonly<{
  inputRef: RefObject<HTMLInputElement>;
  search: string;
  matchCount: number;
  onChange: (value: string) => void;
  onClear: () => void;
}>) {
  const hasQuery = search.trim() !== "";
  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.searchField}>
          <input
            ref={inputRef}
            type="search"
            className={hasQuery ? `${styles.searchInput} ${styles.searchInputClearable}` : styles.searchInput}
            placeholder="Search presentations by name or keyword…"
            value={search}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Search presentation templates"
          />
          {hasQuery && (
            <button type="button" className={styles.searchClearBtn} aria-label="Clear search" title="Clear search (Esc)" onClick={onClear}>
              <svg aria-hidden="true" viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {hasQuery && <output className={styles.matchLine}>{matchCountText(matchCount, search)}</output>}
    </>
  );
}

function PreviewHeader({
  presentation,
  selectedCount,
  isDownloading,
  onBack,
  onSelectAll,
  onDeselectAll,
  onDownload,
}: Readonly<{
  presentation: PresentationTemplate;
  selectedCount: number;
  isDownloading: boolean;
  onBack: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDownload: () => void;
}>) {
  const total = presentation.slides.length;
  const allSelected = selectedCount === total;
  const slideCountLabel = total === 1 ? "1 slide" : `${total} slides`;
  return (
    <div className={styles.previewHeader}>
      <button type="button" className={styles.backBtn} onClick={onBack}>
        ← Back
      </button>
      <div className={styles.previewHeaderTitle}>
        <span className={styles.previewHeaderName} title={presentation.name}>
          {presentation.name}
        </span>
        <span className={styles.previewHeaderCount}>{`· ${slideCountLabel}`}</span>
      </div>
      {selectedCount > 0 && (
        <button type="button" className={styles.selectAllLink} onClick={allSelected ? onDeselectAll : onSelectAll}>
          {allSelected ? "Deselect All" : "Select All"}
        </button>
      )}
      {/* What it downloads (whole deck or just the picks) follows the selection, and so does the label. */}
      <button type="button" className={styles.downloadPackBtn} disabled={isDownloading} onClick={onDownload}>
        {downloadPackLabel(selectedCount, total)}
      </button>
    </div>
  );
}

function PresentationsGrid({
  presentations,
  query,
  isDownloading,
  onOpen,
  onDownload,
  onClearSearch,
}: Readonly<{
  presentations: readonly PresentationTemplate[];
  query: string;
  isDownloading: boolean;
  onOpen: (id: string) => void;
  onDownload: (id: string) => void;
  onClearSearch: () => void;
}>) {
  if (presentations.length === 0) return <NoResults query={query} onClear={onClearSearch} />;
  return (
    <div className={styles.grid}>
      {presentations.map((presentation) => (
        <PresentationCard
          key={presentation.id}
          presentation={presentation}
          onOpen={onOpen}
          onDownload={onDownload}
          downloadDisabled={isDownloading}
        />
      ))}
    </div>
  );
}

function SlidesGrid({
  presentation,
  selectedIds,
  noneInsertable,
  onToggle,
  loadThumbnail,
}: Readonly<{
  presentation: PresentationTemplate;
  selectedIds: ReadonlySet<string>;
  noneInsertable: boolean;
  onToggle: (slide: PresentationTemplateSlide) => void;
  loadThumbnail?: LoadThumbnail;
}>) {
  if (presentation.slides.length === 0) {
    return (
      <div className={styles.centered}>
        <span>This presentation has no slides.</span>
      </div>
    );
  }
  return (
    <div className={styles.grid}>
      {presentation.slides.map((slide, index) => (
        <SlideCard
          key={slide.id}
          slide={slide}
          slideIndex={index + 1}
          selected={selectedIds.has(slide.id)}
          showPreviewOnlyBadge={!slide.insertable && !noneInsertable}
          onToggle={onToggle}
          loadThumbnail={loadThumbnail}
        />
      ))}
    </div>
  );
}

export const PresentationTemplatesPanel = forwardRef<PresentationTemplatesPanelHandle, PresentationTemplatesPanelProps>(
  function PresentationTemplatesPanel({ onSelectionStateChange, templatesOptions }, ref) {
    const {
      loadState,
      insertStatus,
      presentations,
      activePresentation,
      openPresentation,
      closePresentation,
      selectedIds,
      selectedCount,
      toggleSelected,
      retry,
      insertSelected,
      selectAllSlides,
      deselectAllSlides,
      downloadPresentation,
      downloadPack,
      clearSelection,
      isDownloading,
      isInserting,
      insertError,
    } = usePresentationTemplates(templatesOptions);
    const [search, setSearch] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);
    const clearSearch = useCallback(() => setSearch(""), []);
    useEscapeClearsSearch(searchInputRef, clearSearch);

    const visiblePresentations = useMemo(
      () => presentations.filter((presentation) => matchesSearch(presentation, search)),
      [presentations, search],
    );

    // Drives the header's Select All link; and, when no slide can be inserted, one notice replaces per-card badges.
    const activeSelectedCount = countPicked(activePresentation, selectedIds);
    const noneInsertable = hasOnlyPreviewSlides(activePresentation);

    const { sourceName, holderId, handleToggle, handleSelectAll } = usePickGuard({
      presentations,
      selectedIds,
      activePresentation,
      toggleSelected,
      selectAllSlides,
    });

    const revealSelection = useCallback(() => {
      if (holderId !== null) openPresentation(holderId);
    }, [holderId, openPresentation]);

    const view = resolveView(loadState.status, presentations.length);

    useImperativeHandle(ref, () => ({ insertSelected, clearSelection, revealSelection }), [
      insertSelected,
      clearSelection,
      revealSelection,
    ]);

    useReportSelection(onSelectionStateChange, {
      count: selectedCount,
      canInsert: selectedCount > 0 && !isInserting,
      isInserting,
      view,
      sourceName,
    });

    const status = statusScreen(loadState.status, view, retry);
    if (status) return <div className={styles.root}>{status}</div>;

    return (
      <div className={styles.root}>
        {activePresentation === null ? (
          <SearchToolbar
            inputRef={searchInputRef}
            search={search}
            matchCount={visiblePresentations.length}
            onChange={setSearch}
            onClear={clearSearch}
          />
        ) : (
          <PreviewHeader
            presentation={activePresentation}
            selectedCount={activeSelectedCount}
            isDownloading={isDownloading}
            onBack={closePresentation}
            onSelectAll={() => handleSelectAll(activePresentation.id)}
            onDeselectAll={() => deselectAllSlides(activePresentation.id)}
            onDownload={() => downloadPack(activePresentation.id)}
          />
        )}

        {noneInsertable && (
          <div className={styles.previewOnlyNotice}>
            These slides can be previewed but not inserted yet — the library doesn&apos;t publish slide content.
          </div>
        )}

        <InsertBanner error={insertError} status={insertStatus} />

        <div className={styles.body}>
          <div className={styles.gridArea}>
            {activePresentation === null ? (
              <PresentationsGrid
                presentations={visiblePresentations}
                query={search.trim()}
                isDownloading={isDownloading}
                onOpen={openPresentation}
                onDownload={downloadPresentation}
                onClearSearch={clearSearch}
              />
            ) : (
              <SlidesGrid
                presentation={activePresentation}
                selectedIds={selectedIds}
                noneInsertable={noneInsertable}
                onToggle={handleToggle}
                loadThumbnail={templatesOptions?.loadThumbnail}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);
